import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AssignmentCardComponent } from '../assignment-card/assignment-card.component';
import { useBreakpoint } from '../../../../shared/composables/useBreakpoint';
import { UserService } from '../../../user/service/user.service';
import { HomeEmptyStateComponent } from '../home-empty-state/home-empty-state.component';
import { HomeDialogService } from '../../services/home-dialog.service';
import type { ParentDuty } from '../../models/parent-duty.model';
import { HomeDataService } from '../../services/home-data.service';
import { FamilyDutyFeatureService } from '../../services/family-duty-feature.service';
import { ParentFamilyContextService } from '../../../parent-family/parent-family-context.service';
import { FamilyKidsService } from '../../../parent-family/services/family-kids.service';
import { AssignmentStatus } from '../../../user/interfaces/assignment.interface';
import { buildRoutineProgressByDutyId } from '../../helpers/duty-routine-group.helper';

type DutyColumnId = 'new' | 'marked' | 'done';
type DutyFilter = (duty: ParentDuty) => boolean;

interface DutyColumnView {
  id: DutyColumnId;
  titleKey: string;
  cardStatus: AssignmentStatus;
  duties: Array<ParentDuty & { index: number }>;
  count: number;
}

@Component({
  selector: 'app-assignment-board',
  standalone: true,
  imports: [TranslatePipe, AssignmentCardComponent, HomeEmptyStateComponent],
  templateUrl: './assignment-board.component.html',
  styleUrl: './assignment-board.component.scss',
  host: {
    class: 'assignment-board',
    '[class.assignment-board--stacked]': 'isStacked()',
  },
})
export class AssignmentBoardComponent implements OnInit {
  #userService = inject(UserService);
  #homeData = inject(HomeDataService);
  #homeDialogs = inject(HomeDialogService);
  #family = inject(ParentFamilyContextService);
  #familyKids = inject(FamilyKidsService);
  #familyDuty = inject(FamilyDutyFeatureService);
  #router = inject(Router);

  ngOnInit(): void {
    void this.#familyDuty.whenReady();
  }

  selectedCardId = signal<string | null>(null);
  expandedColumns = signal<DutyColumnId[]>(['new']);

  readonly currentBreakpoint = useBreakpoint().currentBreakpoint;
  readonly isStacked = computed(() =>
    ['XSmall', 'Small', 'Medium'].includes(this.currentBreakpoint()),
  );
  readonly isMobile = computed(() => this.currentBreakpoint() === 'XSmall');

  readonly routinesAllowed = computed(() =>
    this.#userService.isQuotaCapabilityAllowed('duties.routines'),
  );

  readonly user = computed(() => this.#userService.user());
  readonly isLoading = computed(
    () => this.#userService.isLoading() || this.#homeData.parentDutiesLoading(),
  );

  readonly isNeutralScope = computed(
    () => !this.#family.selectedChildId()?.trim(),
  );

  readonly hasLinkedChildren = this.#familyKids.hasChildren;

  readonly showSelectChildEmpty = computed(
    () => this.isNeutralScope() && this.hasLinkedChildren(),
  );

  readonly #allDuties = computed(() =>
    this.#homeData.parentDuties().map((duty, index) => ({
      ...duty,
      index,
    })),
  );

  readonly #filterDuties = (filter: DutyFilter) =>
    computed(() => this.#allDuties().filter(filter));

  readonly #isNewDuty: DutyFilter = (duty) =>
    !duty.confirmed && !duty.completed;
  readonly #isMarkedAsSolved: DutyFilter = (duty) =>
    !duty.confirmed && duty.completed;
  readonly #isDone: DutyFilter = (duty) => duty.confirmed && duty.completed;

  readonly newDuties = this.#filterDuties(this.#isNewDuty);
  readonly markedAsSolvedDuties = this.#filterDuties(this.#isMarkedAsSolved);
  readonly doneDuties = this.#filterDuties(this.#isDone);

  readonly allDutiesCount = computed(() => this.#allDuties().length);

  readonly routineProgressByDutyId = computed(() =>
    buildRoutineProgressByDutyId(this.#homeData.parentDuties()),
  );

  readonly columns = computed((): DutyColumnView[] => [
    {
      id: 'new',
      titleKey: 'app.components.assignmentBoard.newAssignments',
      cardStatus: 'new',
      duties: this.newDuties(),
      count: this.newDuties().length,
    },
    {
      id: 'marked',
      titleKey: 'app.components.assignmentBoard.markedAsSolved',
      cardStatus: 'marked',
      duties: this.markedAsSolvedDuties(),
      count: this.markedAsSolvedDuties().length,
    },
    {
      id: 'done',
      titleKey: 'app.components.assignmentBoard.done',
      cardStatus: 'done',
      duties: this.doneDuties(),
      count: this.doneDuties().length,
    },
  ]);

  readonly skeletonColumns = [1, 2, 3] as const;
  readonly skeletonCards = [1, 2] as const;

  isColumnExpanded(id: DutyColumnId): boolean {
    return this.expandedColumns().includes(id);
  }

  isColumnCollapsed(id: DutyColumnId): boolean {
    return this.isStacked() && !this.isColumnExpanded(id);
  }

  toggleColumn(id: DutyColumnId): void {
    if (!this.isStacked()) {
      return;
    }

    this.expandedColumns.update((current) =>
      current.includes(id)
        ? current.filter((columnId) => columnId !== id)
        : [...current, id],
    );
  }

  isCardSelected(cardId: string): boolean {
    return this.selectedCardId() === cardId;
  }

  onCardSelected(cardId: string): void {
    this.selectedCardId.update((current) =>
      current === cardId ? null : cardId,
    );
  }

  onAddNewAssignment(event: Event): void {
    event.stopPropagation();
    this.#homeDialogs.openAddDutyDialog();
  }

  onUseTemplate(event: Event): void {
    event.stopPropagation();
    if (!this.routinesAllowed()) {
      void this.#router.navigate(['/profile'], {
        queryParams: { segment: 'subscriptions' },
      });
      return;
    }
    this.#homeDialogs.openApplyDutyTemplateDialog();
  }

  onEditAssignment(duty: ParentDuty & { index: number }): void {
    this.#homeDialogs.openEditDutyDialog(duty);
  }
}
